import { RAW_MATERIAL_KIND_LABELS } from "@artisancode/api-types";
import { History, Pencil } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, formatCurrency } from "@/lib/utils";

import { StockRiderQtyCell } from "./stock-rider-qty-cell";
import { STOCK_STATUS_CONFIG, type StockItem } from "./types";

export interface StockRowsProps {
  rows: StockItem[];
  onEditMinStock?: (item: StockItem) => void;
  onViewStockCard?: (item: StockItem) => void;
}

export function StockDesktopTable({
  rows,
  onEditMinStock,
  onViewStockCard,
}: StockRowsProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[220px]">Nama Barang</TableHead>
          <TableHead className="w-[130px]">Tipe & Kategori</TableHead>
          <TableHead className="w-[70px]">Satuan</TableHead>
          <TableHead
            className="w-[90px] text-right"
            title="Stok tersegel di toko"
          >
            Di Toko
          </TableHead>
          <TableHead
            className="w-[90px] text-right"
            title="Stok terbuka di bar"
          >
            Di Bar
          </TableHead>
          <TableHead
            className="w-[90px] text-right"
            title="Stok yang dibawa mitra rider"
          >
            Di Rider
          </TableHead>
          <TableHead className="w-[100px] text-right font-bold">
            Total Stok
          </TableHead>
          <TableHead className="w-[90px] text-right">Min. Stok</TableHead>
          <TableHead className="w-[90px] text-center">Status</TableHead>
          <TableHead className="w-[120px] text-right">
            Nilai Persediaan
          </TableHead>
          <TableHead className="w-[80px] text-center">Aksi</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={11}
              className="py-12 text-center text-sm text-muted-foreground"
            >
              Tidak ada barang yang cocok dengan kriteria filter.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((item) => {
            const status = STOCK_STATUS_CONFIG[item.status];

            return (
              <TableRow
                key={item.id}
                className={cn("hover:bg-muted/40", status.rowClassName)}
              >
                <TableCell>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {item.name}
                    </span>
                    {item.sku && (
                      <span className="font-mono text-xs text-muted-foreground">
                        {item.sku}
                      </span>
                    )}
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-col gap-1 items-start">
                    <span className="text-xs font-medium text-foreground">
                      {item.categoryName}
                    </span>
                    {item.type === "raw_material" && item.kind ? (
                      <Badge
                        variant="outline"
                        className="px-1.5 py-0 text-[10px]"
                      >
                        {RAW_MATERIAL_KIND_LABELS[item.kind]}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="px-1.5 py-0 text-[10px]"
                      >
                        Produk Siap Jual
                      </Badge>
                    )}
                  </div>
                </TableCell>

                <TableCell className="text-xs font-medium text-muted-foreground">
                  {item.uomName}
                </TableCell>

                <TableCell className="text-right font-mono text-xs text-muted-foreground">
                  {item.type === "raw_material" && item.kind === "ingredient"
                    ? "—"
                    : item.onHandQty.toLocaleString("id-ID")}
                </TableCell>

                <TableCell className="text-right font-mono text-xs text-muted-foreground">
                  {item.type === "raw_material"
                    ? item.kind === "purchased"
                      ? "—"
                      : item.repackingQty.toLocaleString("id-ID")
                    : "—"}
                </TableCell>

                <TableCell className="text-right font-mono text-xs">
                  <StockRiderQtyCell item={item} />
                </TableCell>

                <TableCell className="text-right">
                  <span
                    className={cn(
                      "font-mono text-sm font-bold",
                      status.qtyClassName,
                    )}
                  >
                    {item.currentStock.toLocaleString("id-ID")}
                  </span>
                </TableCell>

                <TableCell className="text-right">
                  <button
                    type="button"
                    onClick={() => onEditMinStock?.(item)}
                    className="group inline-flex cursor-pointer items-center justify-end gap-1 rounded px-1.5 py-0.5 font-mono text-xs text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
                    title="Klik untuk ubah batas minimum"
                  >
                    <span>{item.minStock.toLocaleString("id-ID")}</span>
                    <Pencil className="h-3 w-3 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                </TableCell>

                <TableCell className="text-center">
                  <Badge
                    variant={status.badgeVariant}
                    className={cn("text-[10px]", status.className)}
                  >
                    {status.label}
                  </Badge>
                </TableCell>

                <TableCell className="text-right font-mono text-xs font-semibold text-foreground">
                  {formatCurrency(item.totalValue)}
                </TableCell>

                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewStockCard?.(item)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:bg-muted/80 hover:text-primary"
                      title={`Lihat kartu stok ${item.name}`}
                    >
                      <History className="h-3.5 w-3.5" />
                      <span className="sr-only">Kartu Stok</span>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditMinStock?.(item)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:bg-muted/80 hover:text-primary"
                      title={`Ubah batas minimum ${item.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="sr-only">Edit Batas Minimum</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
